import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ComingSoon } from './ComingSoon';
import { ImageZoom } from './ImageZoom';

interface Implementation {
  title: string;
  description: string;
  features: string[];
  learnMoreLink?: string;
  exampleLink?: string;
  image?: string;
  additionalImage?: string;
}

export function CookiesManagement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const implementations: Implementation[] = [
    {
      title: "GTM Implementation",
      description: "Cookie blocking through Google Tag Manager with OneTrust integration",
      features: [
        "Managed through GTM container",
        "Dynamic script loading based on consent",
        "Automatic cookie blocking",
        "Real-time consent updates"
      ],
      image: "https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20COOKIES%20PICS/OT%20GTM%20implementation.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBDT09LSUVTIFBJQ1MvT1QgR1RNIGltcGxlbWVudGF0aW9uLnBuZyIsImlhdCI6MTc0NTg2MTEyMSwiZXhwIjoyMDI5Njg1MTIxfQ.g4cvumv-o0xUeaPrGgyFazPbLa3wQf0SEdzVIbtS6lI"
    },
    {
      title: "Auto-Blocking Implementation",
      description: "Auto-blocking prevents source URLs that drop tracking technology from loading on your website until visitors grant consent to them. The auto-block list is populated by scan results and can be edited to unblock functional source URLs that need to load or block ones that were not found in the scan. Changes to auto-block settings will not take effect until you publish them.",
      features: [
        "No manual script modifications",
        "Automatic script detection",
        "Built-in cookie scanning",
        "Zero-configuration blocking"
      ],
      learnMoreLink: "https://my.onetrust.com/s/article/UUID-cdb2fcad-1640-fdbd-e432-73c5536f0bbd?language=en_US",
      exampleLink: "https://www.venomsteel.com/"
    },
    {
      title: "Code Rewrite Implementation",
      description: "Current Setup: Manual Consent Control (Hybrid)",
      features: [
        "We currently use a Hybrid Manual Consent Control setup to manage cookies and tags on your site.",
        "Key points of this setup:",
        "• The Google Tag Manager (GTM) and Google Analytics (GA4) scripts load immediately on page load.",
        "• User consent is collected using OneTrust.",
        "• Behavior (analytics, ads personalization) is manually controlled by updating the consent state after user choice, using gtag('consent', 'update', {...}).",
        "• Cookies are not set until consent is actively given by the user, but the script files are still fetched initially.",
        "This ensures compliance while maintaining excellent site performance.",
        "Example of the current setup (simplified from live code):"
      ],
      image: "https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20COOKIES%20PICS/OT%20GUY%20Code%20Rewrite%20Hybrid.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBDT09LSUVTIFBJQ1MvT1QgR1VZIENvZGUgUmV3cml0ZSBIeWJyaWQucG5nIiwiaWF0IjoxNzQ1ODcyMzIxLCJleHAiOjIwMjk2OTYzMjF9.bwSr4pPXRqXF54eqNtBzTAfao7UdHeQeSxhpVw9wQ9M",
      additionalImage: "https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20COOKIES%20PICS/OT%20GUY%20Full%20Code%20Rewrite.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBDT09LSUVTIFBJQ1MvT1QgR1VZIEZ1bGwgQ29kZSBSZXdyaXRlLnBuZyIsImlhdCI6MTc0NTg3MjM0NiwiZXhwIjoyMDI5Njk2MzQ2fQ.2ZbgRUkwrJVF5u_Ykdv59w5Ip_MH_gwTHr328lSxBN8"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/privacy-center"
            className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Privacy Center
          </Link>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-12">
          <div className="grid gap-8">
            {implementations.map((implementation) => (
              <div 
                key={implementation.title}
                className="bg-gray-50 dark:bg-gray-700 p-8 rounded-xl"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {implementation.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {implementation.description}
                </p>
                {implementation.learnMoreLink && (
                  <a 
                    href={implementation.learnMoreLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#5e9bb8] hover:text-[#499da8] dark:text-[#5e9bb8] dark:hover:text-[#499da8] mb-6 block"
                  >
                    Learn more about Auto-Block
                  </a>
                )}
                {implementation.exampleLink && (
                  <a 
                    href={implementation.exampleLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#5e9bb8] hover:text-[#499da8] dark:text-[#5e9bb8] dark:hover:text-[#499da8] mb-6 block"
                  >
                    Check out this site with OneTrust Auto-Blocking
                  </a>
                )}

                <div className="mb-8">
                  <div className="space-y-2 text-gray-600 dark:text-gray-300">
                    {implementation.features.map((feature, featureIndex) => (
                      <p key={featureIndex}>{feature}</p>
                    ))}
                  </div>
                </div>

                {implementation.image && (
                  <div className="space-y-8">
                    <ImageZoom
                      src={implementation.image}
                      alt={`${implementation.title} Example`}
                      className="w-full h-auto object-contain rounded-lg"
                    />
                    {implementation.additionalImage && (
                      <>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-4">
                          Full Code Rewrite Implementation (Strict Mode)
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          In a Full Code Rewrite, we would delay loading Google Tag Manager and Google Analytics until after the user gives consent.
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Key differences:
                        </p>
                        <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300">
                          <li>Scripts are NOT fetched at all unless user accepts certain cookie categories (Targeting, Analytics, etc.)</li>
                          <li>Even the requests to Google servers are blocked before consent</li>
                          <li>Maximum GDPR compliance (especially important for strict regulators like CNIL in France)</li>
                        </ul>
                        <ImageZoom
                          src={implementation.additionalImage}
                          alt="Full Code Rewrite Implementation Example"
                          className="w-full h-auto object-contain rounded-lg"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Important Note
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                Each implementation approach offers different benefits and trade-offs. Choose the one that best fits your website's needs and technical requirements.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
